import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner, classes } from './ui.tsx';
import {
  dismissPdfDownloadError,
  usePdfDownload,
  usePdfDownloadStatus,
  type PdfDownloadRecovery,
} from '../hooks/use-pdf-download.ts';

/**
 * The two halves of "this PDF is being fetched": the control that was pressed,
 * and a status the whole page carries. See `use-pdf-download.ts` for why a PDF
 * link cannot just be an `<a href>` here.
 */

interface PdfDownloadButtonProps {
  /** Full same-origin path to the PDF, e.g. `/api/v1/invoices/{id}/pdf`. */
  url: string;
  children: ReactNode;
  /**
   * `button` matches the shared Button in that variant; `link` matches an
   * inline text link, for the lists where the download sits among other text
   * rather than in a row of buttons.
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'link';
  /** Only used if the response does not name the file itself. */
  filename?: string;
  className?: string;
  /** Builds the PDF on the fly when `url` turns out to have none yet, or an outdated one. */
  recovery?: PdfDownloadRecovery;
}

export function PdfDownloadButton({
  url,
  children,
  variant = 'secondary',
  filename,
  className,
  recovery,
}: PdfDownloadButtonProps) {
  const { t } = useTranslation();
  const { download, isDownloading } = usePdfDownload();
  const start = () => void download(url, filename, recovery);

  if (variant !== 'link') {
    return (
      <Button variant={variant} busy={isDownloading} onClick={start} className={className}>
        {children}
      </Button>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={isDownloading}
      aria-busy={isDownloading || undefined}
      className={classes(
        'inline-flex items-center gap-2 text-[color:var(--color-brand)] underline disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
    >
      {isDownloading ? <Spinner /> : null}
      {children}
      {/* The visible label stays put while it runs — a label that changes to
          "Preparing…" and back moves everything beside it — so the state is
          spoken here instead. */}
      {isDownloading ? <span className="sr-only">{t('common.pdfPreparing')}</span> : null}
    </button>
  );
}

/**
 * The page-level indicator, mounted once for the whole app.
 *
 * Pinned rather than inline: a download can be started from a control that
 * scrolls away, from a row that a refetch removes, or from a panel that closes
 * behind it, and the answer to "is it still working?" has to survive all three.
 * `role="status"` announces it without stealing focus; the failure takes
 * `role="alert"`, since a download that produced no file is worth interrupting
 * for.
 */
export function PdfDownloadIndicator() {
  const { t } = useTranslation();
  const { active, error } = usePdfDownloadStatus();

  if (active === 0 && !error) return null;

  return (
    <div
      // Named so `SendOutcomeIndicator` can measure this box's real, rendered
      // height and stack itself above it rather than assuming a fixed one —
      // this indicator's own height varies with the error text's length and
      // the viewport's width.
      id="pdf-download-indicator"
      // 1rem above the screen's edge, or above the phone's bottom navigation
      // bar where there is one (index.css).
      className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--shell-bottom-nav,0px)+1rem)] z-50 flex justify-center px-4 print:hidden"
    >
      {active > 0 ? (
        <div
          role="status"
          className="pointer-events-auto flex items-center gap-3 rounded-full border border-[color:var(--color-line)] bg-white px-4 py-2 shadow-lg"
        >
          <Spinner />
          <span className="text-sm font-medium">
            {active > 1 ? t('common.pdfPreparingMany', { count: active }) : t('common.pdfPreparing')}
          </span>
        </div>
      ) : (
        <div
          role="alert"
          className="pointer-events-auto flex items-center gap-3 rounded-lg border border-[color:var(--color-line)] border-l-4 border-l-[color:var(--color-danger)] bg-white px-4 py-2 shadow-lg"
        >
          <span className="text-sm">{error}</span>
          <Button variant="ghost" onClick={dismissPdfDownloadError}>
            {t('common.dismiss')}
          </Button>
        </div>
      )}
    </div>
  );
}
